import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ReportPackingListShoesBySchool.css';
import Swal from 'sweetalert2';
import { reportPackingListShoesBySchoolAPI } from '../../services/api';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';

// Register ag-grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * ReportPackingListShoesBySchool Component
 *
 * Displays packing list report for shoes aggregated by school
 * Features:
 * - District and Activity filtering
 * - Aggregated totals by school
 * - Shows shoe size distribution (sizes 12, 13, 1-11)
 * - Excel/PDF export functionality
 */
const ReportPackingListShoesBySchool = () => {
  // ========== TRANSLATION ==========
  const { t } = useTranslation(['reports', 'common']);

  // ========== STATE MANAGEMENT ==========

  // Auth/User State
  const [user, setUser] = useState(null);
  const [resourcePermissions, setResourcePermissions] = useState(null);

  // Filter State
  const [districts, setDistricts] = useState([]);
  const [activities, setActivities] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');

  // Data State
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ag-grid State
  const [gridApi, setGridApi] = useState(null);

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // ========== AG-GRID CONFIGURATION ==========

  // Column definitions for ag-grid with grouping
  const columnDefs = useMemo(() => [
    {
      headerName: 'NO',
      valueGetter: 'node.rowIndex + 1',
      width: 70,
      cellStyle: { textAlign: 'center' }
    },
    {
      headerName: 'Kod',
      field: 'kod',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    {
      headerName: 'Sekolah',
      field: 'sekolah',
      width: 250,
      // rowGroup: true, // ❌ Removed - requires AG Grid Enterprise
      hide: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    {
      headerName: 'Kasut 12',
      field: 'kasut_12',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 13',
      field: 'kasut_13',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 1',
      field: 'kasut_1',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 2',
      field: 'kasut_2',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 3',
      field: 'kasut_3',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 4',
      field: 'kasut_4',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 5',
      field: 'kasut_5',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 6',
      field: 'kasut_6',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 7',
      field: 'kasut_7',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 8',
      field: 'kasut_8',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 9',
      field: 'kasut_9',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 10',
      field: 'kasut_10',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut 11',
      field: 'kasut_11',
      width: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => params.value || 0
    },
    {
      headerName: 'Kasut Jumlah',
      field: 'kasut_Jumlah',
      width: 120,
      cellStyle: { textAlign: 'center', fontWeight: 'bold' },
      valueFormatter: (params) => params.value || 0
    }
  ], []);

  // Default column configuration
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false,
    wrapHeaderText: true,
    autoHeaderHeight: true
  }), []);

  // Grid options with grouping
  const gridOptions = useMemo(() => ({
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [30, 50, 100, 200],
    domLayout: 'autoHeight',
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    groupDisplayType: 'multipleColumns',
    groupDefaultExpanded: 0,
    autoGroupColumnDef: {
      headerName: 'Group',
      minWidth: 300,
      cellRendererParams: {
        suppressCount: false
      }
    }
  }), []);

  // Grid ready callback
  const onGridReady = (params) => {
    setGridApi(params.api);
    console.log('[ReportPackingListShoesBySchool] ag-grid ready with grouping');
  };

  // ========== EFFECTS ==========

  // Load user and permissions on mount
  useEffect(() => {
    loadUser();
    loadResourcePermissions();
  }, []);

  // Load districts when user is loaded
  useEffect(() => {
    if (user) {
      loadDistricts();
    }
  }, [user]);

  // Load activities when district changes
  useEffect(() => {
    if (selectedDistrict) {
      loadActivities(selectedDistrict);
      setSelectedActivity('');
      setReportData(null);
    }
  }, [selectedDistrict]);

  // Auto-load data when activity changes
  useEffect(() => {
    if (selectedActivity && selectedDistrict) {
      console.log('[ReportPackingListShoesBySchool] Activity selected, auto-loading data');
      loadReportData(selectedDistrict, selectedActivity);
    }
  }, [selectedActivity]);

  // ========== DATA LOADING FUNCTIONS ==========

  const loadUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        setUser(userData);
        console.log('[ReportPackingListShoesBySchool] User loaded:', userData.email);
      } else {
        console.error('[ReportPackingListShoesBySchool] No user found in localStorage');
        setError('User not found. Please login again.');
      }
    } catch (error) {
      console.error('[ReportPackingListShoesBySchool] Error loading user:', error);
      setError('Failed to load user information');
    }
  };

  const loadResourcePermissions = () => {
    try {
      const permsStr = localStorage.getItem('resourcePermissions');
      if (permsStr) {
        const perms = JSON.parse(permsStr);
        setResourcePermissions(perms);
        console.log('[ReportPackingListShoesBySchool] Resource permissions loaded');
      }
    } catch (error) {
      console.error('[ReportPackingListShoesBySchool] Error loading permissions:', error);
    }
  };

  const loadDistricts = async () => {
    try {
      console.log('[ReportPackingListShoesBySchool] Loading districts...');
      const response = await reportPackingListShoesBySchoolAPI.getDistricts();

      if (response.success && response.data) {
        setDistricts(response.data);
        console.log('[ReportPackingListShoesBySchool] Loaded', response.data.length, 'districts');

        // Auto-select if only one district
        if (response.data.length === 1) {
          setSelectedDistrict(response.data[0].ppd);
        }
      } else {
        console.error('[ReportPackingListShoesBySchool] Failed to load districts:', response);
        setError('Failed to load districts');
      }
    } catch (error) {
      console.error('[ReportPackingListShoesBySchool] Error loading districts:', error);
      setError('Error loading districts: ' + error.message);
    }
  };

  const loadActivities = async (districtCode) => {
    try {
      console.log('[ReportPackingListShoesBySchool] Loading activities for district:', districtCode);
      const response = await reportPackingListShoesBySchoolAPI.getActivities(districtCode);

      if (response.success && response.data) {
        setActivities(response.data);
        console.log('[ReportPackingListShoesBySchool] Loaded', response.data.length, 'activities');

        // Auto-select first active activity
        const activeActivity = response.data.find(a => a.status === 'ACTIVE');
        if (activeActivity) {
          setSelectedActivity(activeActivity.activityId.toString());
        } else if (response.data.length > 0) {
          setSelectedActivity(response.data[0].activityId.toString());
        }
      } else {
        console.error('[ReportPackingListShoesBySchool] Failed to load activities:', response);
        setError('Failed to load activities');
      }
    } catch (error) {
      console.error('[ReportPackingListShoesBySchool] Error loading activities:', error);
      setError('Error loading activities: ' + error.message);
    }
  };

  const loadReportData = async (districtCode, activityId) => {
    try {
      setLoading(true);
      setError('');
      console.log('[ReportPackingListShoesBySchool] Loading report data for district:', districtCode, 'activity:', activityId);

      const response = await reportPackingListShoesBySchoolAPI.getReportData(districtCode, parseInt(activityId));

      if (response.success && response.data) {
        setReportData(response.data);
        console.log('[ReportPackingListShoesBySchool] Report data loaded:', response.data.totalShoes, 'shoes from', response.data.totalSchools, 'schools');
      } else {
        console.error('[ReportPackingListShoesBySchool] Failed to load report data:', response);
        setError(response.message || 'Failed to load report data');
        setReportData(null);
      }
    } catch (error) {
      console.error('[ReportPackingListShoesBySchool] Error loading report data:', error);
      setError('Error loading report data: ' + error.message);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // ========== REPORT ISSUE HANDLER ==========

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: t('reports:common.messages.capturingScreenshot'),
        html: t('reports:common.messages.pleaseWaitScreenshot'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.captureFailed'),
        text: t('reports:common.messages.captureFailedMessage')
      });
    }
  };

  // ========== EXPORT FUNCTIONS ==========

  const handleExportExcel = async () => {
    // Validation
    if (!selectedDistrict || !selectedActivity) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.selectionRequired'),
        text: t('reports:packingListShoesBySchool.messages.selectDistrictActivity'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      Swal.fire({
        title: t('reports:common.messages.generatingExcel'),
        html: t('reports:common.messages.pleaseWait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await reportPackingListShoesBySchoolAPI.exportToExcel({
        DistrictCode: selectedDistrict,
        ActivityId: parseInt(selectedActivity),
        SplitBy: 1,
        IncludeSubtotals: true,
        IncludeGrandTotal: true,
        ShowPageHeaders: true,
        ApplyFormatting: true
      });

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        text: t('reports:common.messages.excelDownloaded'),
        confirmButtonColor: '#28a745'
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: error.response?.data?.message || error.message || 'Unknown error',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const handleExportPDF = async () => {
    if (!reportData || !reportData.rows || reportData.rows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.noData'),
        text: t('reports:common.messages.noDataToExport'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      console.log('[ReportPackingListShoesBySchool] Exporting to PDF...');

      Swal.fire({
        title: t('reports:common.messages.generatingPDF'),
        text: t('reports:common.messages.pleaseWaitPDF'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for wide table
      let yPosition = 15;

      // Add company name
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('KIM TECK CHEONG MULIA SDN BHD', pdf.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Add title
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'bold');
      pdf.text('Packing List - Shoes by School', pdf.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Add report info in two columns
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');

      // Left column
      const leftX = 14;
      const rightX = pdf.internal.pageSize.getWidth() / 2 + 10;
      let leftY = yPosition;
      let rightY = yPosition;

      pdf.text(`DAERAH: ${reportData.districtCode}`, leftX, leftY);
      leftY += 5;
      pdf.text(`AKTIVITI: ${reportData.activityName} (${reportData.year})`, leftX, leftY);
      leftY += 5;
      pdf.text(`BIL. SEKOLAH: ${reportData.totalSchools}`, leftX, leftY);
      leftY += 5;
      pdf.text(`JUMLAH PASANG: ${reportData.totalShoes || 0}`, leftX, leftY);
      leftY += 5;

      // Right column - Printed info
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userRole = userData?.role || 'Unknown';
      const currentDate = new Date();
      const dateStr = currentDate.toLocaleDateString('en-MY', { year: 'numeric', month: '2-digit', day: '2-digit' });
      const timeStr = currentDate.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

      pdf.text(`TARIKH CETAK: ${dateStr}`, rightX, rightY);
      rightY += 5;
      pdf.text(`MASA CETAK: ${timeStr}`, rightX, rightY);
      rightY += 5;
      pdf.text(`DICETAK OLEH: ${userData?.username || 'Unknown'} (${userRole})`, rightX, rightY);
      rightY += 5;

      yPosition = Math.max(leftY, rightY) + 3;

      // Prepare table data with grouped headers (3 rows: category, MM, sizes)
      const tableHeaders = [
        [
          { content: 'NO', rowSpan: 3 },
          { content: 'KOD', rowSpan: 3 },
          { content: 'SEKOLAH', rowSpan: 3 },
          { content: 'SAIZ KASUT (MM)', colSpan: 12 },
          { content: 'JUMLAH', rowSpan: 3 }
        ],
        [
          '192', '200', '208', '216', '224', '232', '241', '249', '257', '265', '273', '281'
        ],
        [
          '12', '13', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'
        ]
      ];

      const tableData = reportData.rows.map((row, index) => [
        index + 1,
        row.kod || '',
        row.sekolah || '',
        row.kasut_12 || 0,
        row.kasut_13 || 0,
        row.kasut_1 || 0,
        row.kasut_2 || 0,
        row.kasut_3 || 0,
        row.kasut_4 || 0,
        row.kasut_5 || 0,
        row.kasut_6 || 0,
        row.kasut_7 || 0,
        row.kasut_8 || 0,
        row.kasut_9 || 0,
        row.kasut_10 || 0,
        row.kasut_Jumlah || 0
      ]);

      // Calculate totals row
      const totalsRow = ['', '', 'JUMLAH'];
      // Totals for shoe sizes (cols 3-14)
      for (let col = 3; col < 15; col++) {
        const total = tableData.reduce((sum, row) => sum + (Number(row[col]) || 0), 0);
        totalsRow.push(total);
      }
      // Total for JUMLAH column (col 15)
      const grandTotal = tableData.reduce((sum, row) => sum + (Number(row[15]) || 0), 0);
      totalsRow.push(grandTotal);
      tableData.push(totalsRow);

      // Add table using autoTable
      autoTable(pdf, {
        head: tableHeaders,
        body: tableData,
        startY: yPosition,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 1,
          halign: 'center',
          valign: 'middle',
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.2
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: 0,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 9,
          lineColor: [0, 0, 0],
          lineWidth: 0.2
        },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },   // NO
          1: { halign: 'center' },  // KOD - auto-fit
          2: { halign: 'left' },    // SEKOLAH - auto-fit, left-aligned
          15: { fontStyle: 'bold' } // JUMLAH column
        },
        didParseCell: function(data) {
          // Make JUMLAH row bold
          if (data.section === 'body' && data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
          }
        },
        margin: { left: 5, right: 5 },
        didDrawPage: function (data) {
          // Add page footer
          pdf.setFontSize(9);
          pdf.setFont(undefined, 'normal');
          const pageStr = `Halaman ${pdf.internal.getCurrentPageInfo().pageNumber}`;
          pdf.text(pageStr, pdf.internal.pageSize.getWidth() - 30, pdf.internal.pageSize.getHeight() - 10);
        }
      });

      // Save file
      const filename = `Packing_List_Shoes_BySchool_${reportData.districtCode}_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);

      Swal.close();
      console.log('[ReportPackingListShoesBySchool] PDF export completed');
    } catch (error) {
      console.error('[ReportPackingListShoesBySchool] PDF export failed:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: t('reports:common.messages.exportFailedMessage'),
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // ========== RENDER ==========

  return (
    <div className="report-packing-list-shoes-by-school-container">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2>
          {t('reports:packingListShoesBySchool.title')} ({t('reports:packingListShoesBySchool.titleEn')})
        </h2>
        <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('common:reportIssue', { lng: 'en' })}>
          <i className="fas fa-bug"></i>
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="filters-container">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="ddlDistrict">{t('reports:common.filters.districtPPD')}</label>
            <select
              id="ddlDistrict"
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              disabled={loading}
              className="form-control"
            >
              <option value="">{t('reports:studentList.filters.selectDistrict')}</option>
              {districts.map(district => (
                <option key={district.ppd} value={district.ppd}>
                  {district.ppd}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="ddlActivity">{t('reports:common.filters.activityYear')}</label>
            <select
              id="ddlActivity"
              value={selectedActivity}
              onChange={(e) => setSelectedActivity(e.target.value)}
              disabled={loading || !selectedDistrict}
              className="form-control"
            >
              <option value="">{t('reports:studentList.filters.selectActivity')}</option>
              {activities.map(activity => (
                <option key={activity.activityId} value={activity.activityId}>
                  {activity.activityDisplay}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <button
              onClick={() => loadReportData(selectedDistrict, selectedActivity)}
              disabled={loading || !selectedDistrict || !selectedActivity}
              className="btn btn-primary"
            >
              {loading ? t('reports:studentList.buttons.loading') : t('reports:studentList.buttons.loadReport')}
            </button>
          </div>
        </div>
      </div>

      {reportData && (
        <>
          <div className="report-info">
            <div className="info-card">
              <span className="info-label">{t('reports:packingListShoesBySchool.stats.district')}</span>
              <span className="info-value">{reportData.districtCode}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:packingListShoesBySchool.stats.activity')}</span>
              <span className="info-value">{reportData.activityName}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:packingListShoesBySchool.stats.year')}</span>
              <span className="info-value">{reportData.year}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:packingListShoesBySchool.stats.school')}</span>
              <span className="info-value">{reportData.totalSchools}</span>
            </div>
            <div className="info-card">
              <span className="info-label">{t('reports:packingListShoesBySchool.stats.totalShoes')}</span>
              <span className="info-value">{reportData.totalShoes}</span>
            </div>
          </div>

          <div className="export-buttons">
            <button onClick={handleExportExcel} className="btn btn-success">
              {t('reports:common.actions.exportExcel')}
            </button>
            <button onClick={handleExportPDF} className="btn btn-danger">
              {t('reports:common.actions.exportPDF')}
            </button>
          </div>

          <div className="ag-grid-container ag-theme-alpine">
            <AgGridReact
              columnDefs={columnDefs}
              rowData={reportData.rows}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onGridReady={onGridReady}
            />
          </div>
        </>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: 'Report Packing List Shoes By School',
            selectedDistrict,
            selectedActivity,
            reportData
          }}
        />
      )}
    </div>
  );
};

export default ReportPackingListShoesBySchool;
